// src/context/site/SiteConnectorCard.jsx
//
// The "Connettori" card at the top of the Knowledge Base page (KB-main-docs.png):
// the connected site, the status chip, and [Gestisci], which opens the Site
// Manager modal. Rendered ONLY for a tenant admin (every Site Manager endpoint,
// the GETs included, is tenant-admin) — the parent gates it on canWrite.
//
// This card OWNS the status poller (useKbStatus) and hands `status` +
// `refreshStatus` to the modal, so the page never runs two pollers on the
// same endpoint. The site is re-read whenever the modal closes, because the
// modal can change it.
//
// `is_sentinel: true` (the provisioning placeholder https://example.invalid/)
// is an EMPTY STATE — never rendered as a URL, never clickable.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { getWebsiteSite } from '../contextApi.js';
import { siteErrorCopy } from './siteErrorCopy.js';
import { kbFailureHint, showKbFailure } from './kbFailureCopy.js';
import useKbStatus from './useKbStatus.js';
import { useRunFinishedEpoch } from '../../onboarding/onboardingStore.js';
import KbStatusChip from './KbStatusChip.jsx';
import KbJobBanner from './KbJobBanner.jsx';
import SiteManagerModal from './SiteManagerModal.jsx';
import SectionCard, { CARD_BODY } from '../../components/ui/SectionCard.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

export default function SiteConnectorCard({ tenantId, userKey = '' }) {
  const { t } = useTranslation('context');
  const [site, setSite] = useState(null);        // { site, is_sentinel, etag }
  const [siteError, setSiteError] = useState(null);
  const [open, setOpen] = useState(false);
  const { status, refresh, busy, markStarted, lastFinished, clearFinished } = useKbStatus({ tenantId });

  const loadSite = useCallback(async () => {
    if (!tenantId) return;
    try {
      setSite(await getWebsiteSite({ tenant: tenantId }));
      setSiteError(null);
    } catch (e) {
      setSite(null);
      setSiteError(e);
    }
  }, [tenantId]);

  useEffect(() => {
    setSite(null);
    setSiteError(null);
    setOpen(false);
    loadSite();
  }, [loadSite]);

  // A personalization run writes the connected site and rewrites the crawl list, so
  // re-read both on the onboarding store's `runFinished` epoch — only a bump that
  // happens while MOUNTED (the ref starts at the current epoch).
  const finishedEpoch = useRunFinishedEpoch();
  const handledEpochRef = useRef(finishedEpoch);
  useEffect(() => {
    if (finishedEpoch === handledEpochRef.current) return;
    handledEpochRef.current = finishedEpoch;
    loadSite();
    refresh();
  }, [finishedEpoch, loadSite, refresh]);

  const connected = site && site.site && !site.is_sentinel ? site.site : null;
  // 404 RESOURCE_NOT_FOUND = the tenant owns no brand-guidelines file: the site is set
  // by the operator — a hint, not an error.
  const noBrandFile = siteError?.code === 'RESOURCE_NOT_FOUND';
  const state = status?.state;
  // The address was saved again since the last job failed: an address failure is no longer the user's to fix.
  // Reset by the next job (any state other than Failed).
  const [addressFixed, setAddressFixed] = useState(false);
  useEffect(() => { if (state !== 'Failed') setAddressFixed(false); }, [state]);

  return (
    // The shared section card (2026-09-27, the Gestione Workspace look); the body
    // below is unchanged.
    <SectionCard as="section" icon={Globe} title={t('site.title')}>
      <div style={CARD_BODY}>
      <div style={innerBox}>
        {/* v201: what the site is for sits on the "Sito aziendale" line, right above the site (no card subtitle). */}
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: COLORS.ink }} title={t('site.siteHelp')}>
            {t('site.siteLabel')}
          </label>
          <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{t('site.subtitle')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={readonlyField} data-testid="connected-site">
            {connected || <span style={{ color: COLORS.textTertiary }}>{t('site.notConnected')}</span>}
          </div>
          <KbStatusChip state={state} startedAt={status?.started_at} />
          {/* Locked while a job runs: the modal would be read-only anyway, and the
              banner below already says what is happening and for how long. */}
          <button
            type="button"
            onClick={() => { clearFinished(); setOpen(true); }}
            disabled={!tenantId || busy}
            title={busy ? t('site.manageBusy') : undefined}
            style={primaryBtn(!tenantId || busy)}
          >
            {t('site.manage')}
          </button>
        </div>
        {noBrandFile && <p style={hint(COLORS.textSecondary)}>{t('site.noBrandFile')}</p>}
        {siteError && !noBrandFile && <p role="alert" style={hint(SEMANTIC.errore.fg)}>{siteErrorCopy(t, siteError)}</p>}
        <KbJobBanner status={status} compact />
        {lastFinished && !busy && state === 'Ready' && (
          <p style={hint(SEMANTIC.verde.fg)} data-testid="kb-job-done">{t('site.jobDone', { time: formatTime(lastFinished.at) })}</p>
        )}
        {state === 'Needs review' && <p style={hint(SEMANTIC.attenzione.fg)}>{t('site.reviewHint')}</p>}
        {state === 'Failed' && showKbFailure(status?.failure_code, addressFixed) && (
          <p style={hint(SEMANTIC.errore.fg)}>{kbFailureHint(t, status?.failure_code)}</p>
        )}
      </div>

      {open && (
        <SiteManagerModal
          tenantId={tenantId}
          userKey={userKey}
          site={site}
          status={status}
          refreshStatus={refresh}
          onSiteChanged={loadSite}
          addressFixed={addressFixed}
          onSiteSaved={() => { if (state === 'Failed') setAddressFixed(true); }}
          onJobStarted={markStarted}
          onClose={() => { setOpen(false); loadSite(); refresh(); }}
        />
      )}
      </div>
    </SectionCard>
  );
}

// ── styles ────────────────────────────────────────────────────────────────
const innerBox = {
  border: `1px solid ${COLORS.border}`, borderRadius: RADII.card, padding: '16px 20px',
};

const readonlyField = {
  flex: '1 1 320px', minHeight: '38px', display: 'flex', alignItems: 'center',
  padding: '0 14px', borderRadius: RADII.controlInner, background: COLORS.field,
  fontSize: '13px', color: COLORS.ink, wordBreak: 'break-all',
};

function formatTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function hint(color) {
  return { margin: '10px 0 0', fontSize: '12px', color, lineHeight: 1.5 };
}

function primaryBtn(disabled) {
  return {
    padding: '9px 18px', borderRadius: RADII.controlInner, border: 'none',
    background: disabled ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
